import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, SlidersHorizontal } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { updatePlanFeatures } from '../../services/api';

/** Platform admin grid: which features each plan includes. Workspaces can override it from their edit dialog. */
export default function PlanFeatures({ data, onSaved }) {
  const [pending, setPending] = useState(null); // { plan, feature, on }
  const [saving, setSaving] = useState(false);

  if (!data) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 mb-6">
        <CardContent className="py-6 flex justify-center">
          <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const label = (list, key) => list.find((x) => x.key === key)?.label || key;

  const save = async (e) => {
    e.preventDefault(); // keep the dialog open until the request settles
    if (saving) return;
    setSaving(true);
    try {
      const updated = await updatePlanFeatures({ [pending.plan]: { [pending.feature]: pending.on } });
      onSaved(updated);
      toast.success(`${label(data.features, pending.feature)} turned ${pending.on ? 'on' : 'off'} for ${label(data.plans, pending.plan)}`);
      setPending(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update plan features');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 mb-6" data-testid="plan-features">
      <CardHeader className="pb-3">
        <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-emerald-500" /> Plans &amp; features
        </CardTitle>
        <CardDescription className="text-zinc-500">
          What each plan includes. A workspace set to On or Off in its settings ignores this.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-zinc-500 text-xs uppercase tracking-wider">
                <th className="text-left font-normal py-2 pr-4">Feature</th>
                {data.plans.map((p) => (
                  <th key={p.key} className="text-center font-normal py-2 px-4">{p.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.features.map((f) => (
                <tr key={f.key} className="border-t border-zinc-800">
                  <td className="py-3 pr-4 text-zinc-200">{f.label}</td>
                  {data.plans.map((p) => {
                    const on = !!data.planFeatures[p.key]?.[f.key];
                    return (
                      <td key={p.key} className="py-3 px-4 text-center">
                        <Switch
                          checked={on}
                          onCheckedChange={(v) => setPending({ plan: p.key, feature: f.key, on: v })}
                          aria-label={`${f.label} for ${p.label}`}
                          data-testid={`plan-feature-${p.key}-${f.key}`}
                          className="data-[state=checked]:bg-emerald-600 data-[state=unchecked]:bg-zinc-700"
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>

      <AlertDialog open={!!pending} onOpenChange={(open) => { if (!open && !saving) setPending(null); }}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 max-w-[90vw] sm:max-w-md">
          {pending && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-zinc-100">
                  Turn {pending.on ? 'on' : 'off'} {label(data.features, pending.feature)} for {label(data.plans, pending.plan)}?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-zinc-400">
                  {pending.on
                    ? `Every ${label(data.plans, pending.plan)} workspace gets it now, except workspaces set to Off.`
                    : `${label(data.plans, pending.plan)} workspaces lose it now, except workspaces set to On.${
                      pending.feature === 'campaigns' ? ' Live campaigns can still be rolled back; scheduled launches will not run.' : ''}`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-zinc-700" disabled={saving}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="plan-feature-confirm">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Turn ${pending.on ? 'on' : 'off'}`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
